import React, { useState, useEffect, useMemo } from "react";
import { Formik, Form, FormikHelpers } from "formik";
import * as Yup from "yup";

//MUI
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";

import FormikControl from "../../components/formik/FormikControl";
import Loader from "../../components/loader/Loader";

import UserService from "../../services/UserService";
import { Mode } from "./UserPage";
import { UserModel } from "../../models/UserModel";
import { createAutocompleteOptions } from "../../utility/utils";
import { fetchUsers } from "../../redux/actions/UserActions";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { appConfig } from "../../utility/config";

const validationSchema = Yup.object({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string(),
  emailId: Yup.string().required("Required"),
  password: Yup.string().required("Required"),
  roleId: Yup.number().min(1, "Required").required("Required"),
});

type FormProps = Yup.InferType<typeof validationSchema>;

type Props = {
  mode: Mode;
  handleModalClose: () => void;
  data?: UserModel;
};

const UserForm = ({ mode, handleModalClose, data }: Props) => {
  const initialValues = useMemo(
    () => ({
      firstName: "",
      emailId: "",
      lastName: "",
      password: "",
      roleId: 0,
    }),
    []
  );

  const [formState, setFormState] = useState<FormProps>(initialValues);
  const dispatch = useAppDispatch();
  const displayToast = useAppSelector((state) => state.toast.displayToast);
  const allRoles = useAppSelector((state) => state.role.all_Roles);

  const roleOptions = useMemo(() => {
    return createAutocompleteOptions(allRoles, "roleId", "name");
  }, [allRoles]);

  useEffect(() => {
    if (mode === "edit" && data) {
      setFormState(() => ({
        firstName: data.firstName,
        lastName: data.lastName,
        emailId:
          process.env.NODE_ENV === "production" ? data.emailId : data.loginid,
        password: data.password!,
        roleId: data.roleId,
      }));
    }
  }, [mode, data]);

  const submitHandler = async (
    values: FormProps,
    actions: FormikHelpers<FormProps>
  ) => {
    try {
      let formData: UserModel = {
        userId: 0,
        firstName: "",
        lastName: "",
        emailId: "",
        loginid: "",
        roleId: 0,
        isActive: true,
        isAdmin: false,
        password: appConfig.loginPassword,
        createdDate: new Date(),
        modifiedDate: new Date(),
      };
      if (mode === "add") {
        formData.firstName = values.firstName;
        formData.lastName = values.lastName || "";
        formData.emailId = values.emailId;
        formData.loginid = values.emailId;
        formData.roleId = values.roleId;
      } else if (data) {
        formData = {
          ...data,
          firstName: values.firstName,
          lastName: values.lastName || "",
          emailId: values.emailId,
          loginid: values.emailId,
          roleId: values.roleId,
          modifiedDate: new Date(),
        };
      }
      await UserService.createUser(formData, data?.userId);
      actions.setSubmitting(false);
      handleModalClose();
      dispatch(fetchUsers());
      displayToast(
        `User ${mode === "add" ? "created" : "updated"} successfully`,
        "success"
      );
    } catch (error) {
      actions.setSubmitting(false);
    }
  };
  return (
    <Formik
      initialValues={formState}
      validationSchema={validationSchema}
      onSubmit={submitHandler}
      enableReinitialize={true}
    >
      {(formik) => (
        <>
          <Form>
            <Stack gap={2}>
              <FormikControl
                control="input"
                type="text"
                name="firstName"
                size="small"
                label="First Name"
                placeholder="Enter First Name"
              />
              <FormikControl
                control="input"
                name="lastName"
                size="small"
                label="Last Name"
                placeholder="Enter Last Name"
              />

              <FormikControl
                control="input"
                type="email"
                name="emailId"
                size="small"
                label="Email"
                placeholder="Enter Email"
                disabled={mode === "edit" ? true : false}
              />
              {mode === "add" ? (
                <FormikControl
                  control="input"
                  type="text"
                  name="password"
                  size="small"
                  label="Password"
                  placeholder="Enter Password"
                />
              ) : null}
              <FormikControl
                control="autocomplete"
                options={roleOptions}
                name="roleId"
                size="small"
                label="Role"
                placeholder="Select Role"
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={formik.isSubmitting}
              >
                Submit
              </Button>
            </Stack>
          </Form>
          {formik.isSubmitting && <Loader />}
        </>
      )}
    </Formik>
  );
};

export default UserForm;
